import { useMutation } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { onAuditLogMessage } from './cacheKeys';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';

const intl = getIntl('useProfileMessages', {
    sendMessageSuccess: 'Message sent',
    sendMessageError: 'Error sending message',
});

export type UseProfileMessagesArgs = {
    onSendMessageSuccess?: () => void;
};

export type SendMessageArgs = {
    medicalProfileIds: string[];
    message: string;
};

export type UseAuditLogReturnValue = {
    sendMessage: (args: SendMessageArgs) => void;
    isSendingMessage: boolean;
};

export const useProfileMessages = (args: UseProfileMessagesArgs) => {
    const notify = useNotify();
    const sendMessage = useMutation({
        mutationFn: async (args: SendMessageArgs) => {
            const submitProfileMessage = async (medicalProfileId: string) => {
                const body: APITypes.auditLogService.AddProfileMessageAuditLogRequest = {
                    type: 'profile-message',
                    message: args.message,
                };
                await appAPI.post(urls.api.auditLogs.auditLogMessage(medicalProfileId), { body });
            };

            await Promise.all(args.medicalProfileIds.map(submitProfileMessage));
        },
        onSuccess: (_response, input) => {
            notify('success', intl('sendMessageSuccess'));

            onAuditLogMessage.resetCache({ medicalProfileIds: input.medicalProfileIds })();
            args.onSendMessageSuccess?.();
        },
        onError: () => {
            notify('error', intl('sendMessageError'));
        },
    });

    return {
        isSendingMessage: sendMessage.isPending,
        sendMessage: sendMessage.mutate,
    };
};
